/**
 * Arrival-airport info for dynamic packages (hotel + flight).
 *
 * The cheapest-flight search picks the nearest airport that has a bookable
 * flight, which is not always the airport next to the hotel (a Mykonos hotel
 * can come back with a flight to Athens). The backend reports what it tried
 * (`nearbyAirports`), where the flight lands (`selectedAirport`) and, for a
 * direct-only search, a flight with stops to the nearest airport
 * (`alternativeWithStops`). These helpers read that info and decide how loudly
 * to warn about it.
 */

/** Distance from the hotel above which the arrival airport is flagged. */
export const AIRPORT_DISTANCE_WARNING_KM = 20;
/** Distance from the hotel above which the customer is stopped with a modal. */
export const AIRPORT_DISTANCE_ALERT_KM = 50;

export type AirportSearchStatus = 'no_flights' | 'selected' | 'not_searched';
export type AirportDistanceFrom = 'hotel' | 'location' | 'coordinates';
export type AirportDistanceLevel = 'normal' | 'warning' | 'alert';

export interface NearbyAirport {
  iata_code: string;
  /** tbl_locations id — what the airport picker sends as the new location. */
  location_id?: number | null;
  name: string | null;
  distance_km: number | null;
  latitude?: number | null;
  longitude?: number | null;
  search_status?: AirportSearchStatus;
}

export interface ArrivalAirport {
  iata_code: string | null;
  location_id?: number | null;
  name: string | null;
  distance_km: number | null;
  latitude?: number | null;
  longitude?: number | null;
  searched_iata_code?: string | null;
}

export interface AlternativeWithStops {
  airport: ArrivalAirport;
  flights_bundle?: any[];
  flights?: any[];
}

export interface ArrivalAirportInfo {
  nearbyAirports: NearbyAirport[];
  selectedAirport: ArrivalAirport | null;
  isAlternateAirport: boolean;
  alternativeWithStops: AlternativeWithStops | null;
  distanceFrom: AirportDistanceFrom | null;
}

const EARTH_RADIUS_KM = 6371;
const toRadians = (deg: number) => (deg * Math.PI) / 180;
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** Great-circle distance in km, or null when a coordinate is missing. */
export const getDistanceKm = (
  lat1?: number | string | null,
  lng1?: number | string | null,
  lat2?: number | string | null,
  lng2?: number | string | null
): number | null => {
  const values = [lat1, lng1, lat2, lng2].map((v) =>
    v === null || v === undefined || v === '' ? NaN : Number(v)
  );
  if (!values.every(isFiniteNumber)) return null;
  const [aLat, aLng, bLat, bLng] = values;
  // 0,0 is a missing coordinate, not a place.
  if ((aLat === 0 && aLng === 0) || (bLat === 0 && bLng === 0)) return null;

  const dLat = toRadians(bLat - aLat);
  const dLng = toRadians(bLng - aLng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(aLat)) *
      Math.cos(toRadians(bLat)) *
      Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const getAirportDistanceLevel = (
  distanceKm: number | null | undefined
): AirportDistanceLevel => {
  if (!isFiniteNumber(distanceKm)) return 'normal';
  if (distanceKm > AIRPORT_DISTANCE_ALERT_KM) return 'alert';
  if (distanceKm > AIRPORT_DISTANCE_WARNING_KM) return 'warning';
  return 'normal';
};

/**
 * Both cheapest-flight hooks catch their own failures and hand the error back as
 * *data* (`{ status: 'error', ... }`), so `isError` never fires — a search that
 * produced no bookable flight has to be recognised from the payload.
 */
export const isEmptyFlightResult = (result: any) =>
  !!result && (result.status === 'error' || !result?.data?.flights_bundle?.[0]);

/**
 * Coordinates to search flights around when the hotel has no location id to
 * resolve an arrival airport from.
 *
 * The hotel's own coordinates live on `additional_info` (`latitude`/`longitude`,
 * written from the supplier's hotel record). `location.lat`/`location.long` are
 * the *city* centre and only exist when the hotel is mapped to a location at
 * all — which is exactly the case this fallback does not cover — so they are a
 * secondary source, kept for hotels whose supplier record carried no coords.
 */
export const getHotelCoordinates = (
  hotelData: any
): { latitude: number; longitude: number } | null => {
  const info = hotelData?.additional_info;
  let parsedInfo = info;
  if (typeof info === 'string') {
    try {
      parsedInfo = JSON.parse(info);
    } catch {
      parsedInfo = undefined;
    }
  }

  const latitude = Number(parsedInfo?.latitude ?? hotelData?.location?.lat);
  const longitude = Number(parsedInfo?.longitude ?? hotelData?.location?.long);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  // 0,0 is the null island — a missing coordinate, not a place with airports.
  if (latitude === 0 && longitude === 0) {
    return null;
  }

  return { latitude, longitude };
};

/** Replaces `{{key}}` placeholders, the convention the translation files use. */
export const fillTemplate = (
  template: string | undefined,
  vars: Record<string, string | number | null | undefined>
): string =>
  Object.entries(vars).reduce(
    (text, [key, value]) =>
      text
        .split(`{{${key}}}`)
        .join(value === null || value === undefined ? '' : String(value)),
    template ?? ''
  );

/** "Athens (ATH)", or just the code when the name is unknown. */
export const formatAirportLabel = (
  airport?: {
    iata_code?: string | null;
    name?: string | null;
  } | null
): string => {
  if (!airport?.iata_code) return airport?.name ?? '';
  return airport.name && airport.name !== airport.iata_code
    ? `${airport.name} (${airport.iata_code})`
    : airport.iata_code;
};

const asArray = (value: any) =>
  Array.isArray(value) ? value : value ? [value] : [];

/** Last outbound segment — where the package's flight lands. */
export const getArrivalSegment = (flight: any) => {
  const flightFrom = asArray(flight?.flightFrom);
  return flightFrom[flightFrom.length - 1] ?? null;
};

/** Stops on the outbound leg (segment changes plus technical stops). */
export const getOutboundStops = (flight: any): number => {
  const flightFrom = asArray(flight?.flightFrom);
  if (!flightFrom.length) return 0;
  const technicalStops = flightFrom.reduce(
    (sum: number, segment: any) => sum + (Number(segment?.num_of_stops) || 0),
    0
  );
  return flightFrom.length - 1 + technicalStops;
};

/** Normalises a flight whose legs came back as objects instead of arrays. */
export const normalizeBundleFlight = (flight: any) => {
  if (
    flight?.flightFrom &&
    !Array.isArray(flight.flightFrom) &&
    !Array.isArray(flight.flightBack)
  ) {
    return {
      ...flight,
      flightFrom: [flight.flightFrom],
      flightBack: flight.flightBack ? [flight.flightBack] : [],
    };
  }
  return flight;
};

/** First flight of the alternative, whichever key the search answered with. */
export const getAlternativeFlight = (
  alternative?: AlternativeWithStops | null
) => alternative?.flights_bundle?.[0] ?? alternative?.flights?.[0] ?? null;

/**
 * Reads the arrival-airport info off a cheapest-flight result. Successful
 * results carry it on `data`; the hooks' error results keep it on
 * `airportInfo` (their `data` stays absent so existing "no data = error"
 * checks keep working).
 */
export const getArrivalAirportInfo = (
  result: any
): ArrivalAirportInfo | null => {
  const source = result?.data?.nearbyAirports
    ? result.data
    : (result?.airportInfo ?? null);
  if (!source) return null;
  return {
    nearbyAirports: Array.isArray(source.nearbyAirports)
      ? source.nearbyAirports
      : [],
    selectedAirport: source.selectedAirport ?? null,
    isAlternateAirport: !!source.isAlternateAirport,
    alternativeWithStops: source.alternativeWithStops ?? null,
    distanceFrom: source.distanceFrom ?? null,
  };
};

/** Pulls the airport fields out of a raw response `data` object. */
export const pickArrivalAirportFields = (data: any) => {
  if (!data?.nearbyAirports) return undefined;
  const alternative = data.alternativeWithStops;
  return {
    nearbyAirports: data.nearbyAirports,
    selectedAirport: data.selectedAirport ?? null,
    isAlternateAirport: !!data.isAlternateAirport,
    distanceFrom: data.distanceFrom ?? null,
    alternativeWithStops: alternative
      ? {
          ...alternative,
          ...(alternative.flights_bundle
            ? {
                flights_bundle: alternative.flights_bundle.map(
                  normalizeBundleFlight
                ),
              }
            : {}),
        }
      : null,
  };
};

/** Airports the search tried and found nothing at (no direct flight, if direct-only). */
export const getTriedAirportsWithoutFlights = (
  info?: ArrivalAirportInfo | null
) =>
  (info?.nearbyAirports ?? []).filter((a) => a.search_status === 'no_flights');

// ---------------------------------------------------------------------------
// Hand-off from the results page to the hotel + flight page.
//
// Result cards open the package in a NEW TAB (Redux does not travel), and the
// page opens with the flight already chosen, so it never runs the search that
// knows which airports were tried. The results page stores the info here,
// keyed by the flight identifier the card links with.
// ---------------------------------------------------------------------------

const SNAPSHOT_STORAGE_KEY = 'dpArrivalAirportInfo';
const SNAPSHOT_TTL_MS = 6 * 60 * 60 * 1000;
const SNAPSHOT_MAX_ENTRIES = 20;

type SnapshotStore = Record<
  string,
  {
    savedAt: number;
    info: ArrivalAirportInfo;
    /** The results page already showed its airport modal for this flight. */
    seenOnResults?: boolean;
  }
>;

const readSnapshotStore = (): SnapshotStore => {
  try {
    const raw = window.localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const saveArrivalAirportSnapshot = (
  flightIdentifier: string | null | undefined,
  info: ArrivalAirportInfo | null | undefined
) => {
  if (!flightIdentifier || !info?.nearbyAirports?.length) return;
  try {
    const now = Date.now();
    const store = readSnapshotStore();
    const entries = Object.entries(store)
      .filter(
        ([key, entry]) =>
          key !== flightIdentifier && now - entry.savedAt < SNAPSHOT_TTL_MS
      )
      .sort(([, a], [, b]) => b.savedAt - a.savedAt)
      .slice(0, SNAPSHOT_MAX_ENTRIES - 1);
    const next: SnapshotStore = Object.fromEntries(entries);
    // The alternative flight travels too: the hotel + flight page offers it
    // and re-prices it (view-flight by identifier) once it is picked.
    next[flightIdentifier] = {
      savedAt: now,
      info,
      seenOnResults: store[flightIdentifier]?.seenOnResults,
    };
    window.localStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full or blocked — the page falls back to distances only.
  }
};

export const readArrivalAirportSnapshot = (
  flightIdentifier: string | null | undefined
): ArrivalAirportInfo | null => {
  if (!flightIdentifier) return null;
  const entry = readSnapshotStore()[flightIdentifier];
  if (!entry || Date.now() - entry.savedAt > SNAPSHOT_TTL_MS) return null;
  return entry.info ?? null;
};

/**
 * Marks that the results page showed its airport modal for this flight, so
 * the hotel + flight tab does not pop the same explanation again (its badge
 * and "details" still show it).
 */
export const markArrivalAirportSnapshotSeen = (
  flightIdentifier: string | null | undefined
) => {
  if (!flightIdentifier) return;
  try {
    const store = readSnapshotStore();
    if (!store[flightIdentifier]) return;
    store[flightIdentifier].seenOnResults = true;
    window.localStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Blocked storage only means the modal may show again.
  }
};

export const wasArrivalAirportSeenOnResults = (
  flightIdentifier: string | null | undefined
): boolean => {
  if (!flightIdentifier) return false;
  const entry = readSnapshotStore()[flightIdentifier];
  return (
    !!entry?.seenOnResults && Date.now() - entry.savedAt <= SNAPSHOT_TTL_MS
  );
};

// "Seen it" memory for the alternate-airport modal: shown once per hotel,
// dates and arrival airport in a browser session.
const DISMISSED_STORAGE_KEY = 'dpArrivalAirportModalSeen';

export const wasArrivalAirportModalSeen = (key: string): boolean => {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    return !!raw && JSON.parse(raw)?.includes?.(key);
  } catch {
    return false;
  }
};

export const markArrivalAirportModalSeen = (key: string) => {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    const seen: string[] = raw ? JSON.parse(raw) : [];
    if (!seen.includes(key)) {
      window.sessionStorage.setItem(
        DISMISSED_STORAGE_KEY,
        JSON.stringify([...seen.slice(-49), key])
      );
    }
  } catch {
    // Blocked storage only means the modal may show again.
  }
};
